import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { Button } from "@/components/ui/button";
import { RstScoreBadge } from "@/components/RstScoreBadge";
import { MessageSquare, Search, ShoppingBasket, Package, ClipboardList, TrendingUp } from "lucide-react";

export default function BuyerDashboard() {
  const { user } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);

  const loadConvos = useCallback(() => { api.get("/conversations").then((r) => setConversations(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadConvos(); }, [loadConvos]);
  useAutoRefresh(loadConvos, 15000);

  return (
    <div className="container-page py-10">
      <p className="text-muted-foreground text-sm">{t("dashboard.welcome")}, {user.name}</p>
      <h1 className="font-heading font-extrabold text-3xl text-secondary">{t("dashboard.buyerTitle")}</h1>
      <p className="text-muted-foreground mt-1">{t("dashboard.buyerSubtitle")}</p>
      <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="buyer-rst">
        <RstScoreBadge score={user.rst_score} tier={user.rst_tier} size="md" />
        <span className="text-xs text-muted-foreground">{t("rst.scoreHint")}</span>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        <button onClick={() => navigate("/catalogue")} className="rounded-2xl bg-card border border-border p-6 text-left hover:shadow-lg hover:-translate-y-0.5 transition-[transform,box-shadow]" data-testid="buyer-browse">
          <span className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center"><Search className="h-5 w-5 text-primary" /></span>
          <h3 className="mt-4 font-heading font-semibold text-lg text-secondary">{t("nav.catalogue")}</h3>
          <p className="text-sm text-muted-foreground mt-1">{t("catalog.subtitle")}</p>
        </button>
        <button onClick={() => navigate("/echanges")} className="rounded-2xl bg-card border border-border p-6 text-left hover:shadow-lg hover:-translate-y-0.5 transition-[transform,box-shadow]" data-testid="buyer-messages">
          <span className="h-11 w-11 rounded-xl bg-secondary/10 flex items-center justify-center"><MessageSquare className="h-5 w-5 text-secondary" /></span>
          <h3 className="mt-4 font-heading font-semibold text-lg text-secondary">{t("messages.title")}</h3>
          <p className="text-sm text-muted-foreground mt-1">{conversations.length} {t("admin.conversations").toLowerCase()}</p>
        </button>
        <button onClick={() => navigate("/acheteur/commandes")} className="rounded-2xl bg-card border border-border p-6 text-left hover:shadow-lg hover:-translate-y-0.5 transition-[transform,box-shadow]" data-testid="buyer-orders">
          <span className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center"><Package className="h-5 w-5 text-primary" /></span>
          <h3 className="mt-4 font-heading font-semibold text-lg text-secondary">{t("order.myOrders")}</h3>
          <p className="text-sm text-muted-foreground mt-1">{t("order.guarantee")}</p>
        </button>
        <button onClick={() => navigate("/acheteur/demandes")} className="rounded-2xl bg-card border border-border p-6 text-left hover:shadow-lg hover:-translate-y-0.5 transition-[transform,box-shadow]" data-testid="buyer-requests">
          <span className="h-11 w-11 rounded-xl bg-accent/20 flex items-center justify-center"><ClipboardList className="h-5 w-5 text-primary" /></span>
          <h3 className="mt-4 font-heading font-semibold text-lg text-secondary">{t("request.myRequests")}</h3>
          <p className="text-sm text-muted-foreground mt-1">{t("request.create")}</p>
        </button>
        <button onClick={() => navigate("/mon-evolution")} className="rounded-2xl bg-card border border-border p-6 text-left hover:shadow-lg hover:-translate-y-0.5 transition-[transform,box-shadow]" data-testid="buyer-progress">
          <span className="h-11 w-11 rounded-xl bg-secondary/10 flex items-center justify-center"><TrendingUp className="h-5 w-5 text-secondary" /></span>
          <h3 className="mt-4 font-heading font-semibold text-lg text-secondary">{t("progress.view")}</h3>
          <p className="text-sm text-muted-foreground mt-1">{t("progress.subtitle")}</p>
        </button>
      </div>

      <div className="mt-10">
        <h2 className="font-heading font-bold text-xl text-secondary mb-4">{t("messages.title")}</h2>
        {conversations.length === 0 ? (
          <div className="rounded-2xl bg-card border border-dashed border-border p-10 text-center" data-testid="buyer-no-convos">
            <ShoppingBasket className="h-9 w-9 mx-auto text-muted-foreground" />
            <p className="mt-3 text-muted-foreground">{t("messages.empty")}</p>
            <Button className="mt-4 rounded-full" onClick={() => navigate("/catalogue")}>{t("nav.browse")}</Button>
          </div>
        ) : (
          <div className="grid gap-3">
            {conversations.map((c) => (
              <Link key={c.id} to={`/echanges?c=${c.id}`} className="rounded-xl bg-card border border-border p-4 hover:border-primary transition-colors" data-testid={`convo-${c.id}`}>
                <p className="font-semibold text-secondary">{c.listing_title}</p>
                <p className="text-sm text-muted-foreground truncate">{c.last_message}</p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
